<template>
    <div ref="root" class="filter-multi" :class="{ 'is-open': isOpen, 'is-searchable': searchable }">
        <button
            type="button"
            class="filter-multi__trigger"
            :aria-expanded="isOpen ? 'true' : 'false'"
            aria-haspopup="listbox"
            :title="summary"
            :disabled="disabled"
            @click="toggleOpen"
        >
            <span class="filter-multi__value">{{ summary }}</span>
            <span class="filter-multi__chevron" aria-hidden="true"></span>
        </button>
        <div v-if="isOpen" class="filter-multi__panel">
            <input
                v-if="searchable"
                ref="searchInput"
                v-model="search"
                type="search"
                class="filter-multi__search"
                placeholder="Поиск"
                aria-label="Поиск"
            />
            <div class="filter-multi__options" role="listbox" aria-multiselectable="true">
                <label class="filter-multi__option filter-multi__option--all">
                    <input
                        type="checkbox"
                        class="custom-checkbox"
                        :checked="isAllSelected"
                        @change="selectAll"
                    />
                    <span class="filter-multi__option-label">{{ allLabel }}</span>
                </label>
                <label
                    v-for="option in visibleOptions"
                    :key="option.value"
                    class="filter-multi__option"
                >
                    <input
                        type="checkbox"
                        class="custom-checkbox"
                        :checked="isChecked(option.value)"
                        @change="toggleOption(option.value)"
                    />
                    <span class="filter-multi__option-label">{{ option.label }}</span>
                </label>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

const props = defineProps({
    modelValue: { type: Array, default: () => [] },
    options: { type: Array, default: () => [] },
    allLabel: { type: String, required: true },
    searchable: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const root = ref(null);
const searchInput = ref(null);
const isOpen = ref(false);
const search = ref('');

const selectedLabels = computed(() => {
    const labels = new Map(props.options.map((option) => [String(option.value), option.label]));
    return props.modelValue.map((value) => labels.get(String(value)) || String(value));
});

const summary = computed(() => (
    selectedLabels.value.length === 0 ? props.allLabel : selectedLabels.value.join(', ')
));

const isAllSelected = computed(() => props.modelValue.length === 0);

const visibleOptions = computed(() => {
    const query = search.value.trim().toLocaleLowerCase();
    if (!props.searchable || query === '') {
        return props.options;
    }

    return props.options.filter((option) => String(option.label).toLocaleLowerCase().includes(query));
});

const closePanel = () => {
    isOpen.value = false;
    search.value = '';
};

const toggleOpen = () => {
    if (props.disabled) {
        return;
    }
    if (isOpen.value) {
        closePanel();
        return;
    }
    isOpen.value = true;
};

const selectAll = () => {
    emit('update:modelValue', []);
};

const isChecked = (value) => props.modelValue.some((item) => String(item) === String(value));

const toggleOption = (value) => {
    const key = String(value);
    const current = props.modelValue.map((item) => String(item));
    const next = current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key];
    emit('update:modelValue', next);
};

const onDocumentPointerDown = (event) => {
    if (!root.value?.contains(event.target)) {
        closePanel();
    }
};

const onDocumentKeydown = (event) => {
    if (event.key === 'Escape' && isOpen.value) {
        closePanel();
    }
};

watch(isOpen, (open) => {
    if (!open || !props.searchable) {
        return;
    }
    nextTick(() => searchInput.value?.focus());
});

watch(() => props.disabled, (disabled) => {
    if (disabled) {
        closePanel();
    }
});

onMounted(() => {
    document.addEventListener('pointerdown', onDocumentPointerDown);
    document.addEventListener('keydown', onDocumentKeydown);
});

onBeforeUnmount(() => {
    document.removeEventListener('pointerdown', onDocumentPointerDown);
    document.removeEventListener('keydown', onDocumentKeydown);
});
</script>

<style scoped lang="scss">
.filter-multi {
    position: relative;
    min-width: 0;

    &.is-open {
        z-index: 30;
    }
}

.filter-multi__trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    padding: 6px 10px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 14px;
    font-family: inherit;
    line-height: 1.4;
    text-align: left;
    color: inherit;
    background: #fff;
    cursor: pointer;
    transition: border-color 0.2s;

    &:hover:not(:disabled) {
        border-color: #9ca3af;
    }

    &:focus {
        outline: none;
        border-color: #2563eb;
        box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}

.filter-multi__value {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.filter-multi__chevron {
    flex-shrink: 0;
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 5px solid #6b7280;
}

.filter-multi__panel {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: #fff;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
}

.filter-multi.is-searchable .filter-multi__panel {
    right: auto;
    min-width: 100%;
    width: max-content;
    max-width: 420px;
}

.filter-multi__search {
    display: block;
    width: calc(100% - 8px);
    margin: 4px;
    padding: 6px 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font: inherit;
    font-size: 14px;
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: #2563eb;
        box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
    }
}

.filter-multi__options {
    max-height: 240px;
    overflow-y: auto;
    padding: 4px;
}

.filter-multi__option {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    padding: 6px 8px;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;

    &:hover {
        background: #f8fafc;
    }
}

.filter-multi__option--all {
    margin-bottom: 4px;
    border-bottom: 1px solid #e5e7eb;
    border-radius: 4px 4px 0 0;
}

.filter-multi__option-label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.custom-checkbox {
    -webkit-appearance: none;
    appearance: none;
    display: inline-block;
    position: relative;
    width: 18px;
    height: 18px;
    border: 2px solid #cbd5e1;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.18s ease;

    &:checked {
        background: linear-gradient(180deg, #2563eb, #1d4ed8);
        border-color: transparent;

        &::after {
            content: '';
            position: absolute;
            left: 5px;
            top: 1px;
            width: 4px;
            height: 8px;
            border: solid #fff;
            border-width: 0 2px 2px 0;
            transform: rotate(45deg);
        }
    }
}
</style>
